import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import { Button } from '../../shared/ui/button';
import { ConfirmDelete } from '../../shared/ui/confirm-delete';
import { DangerAction } from '../../shared/ui/danger-zone';
import { MutationError } from '../../shared/ui/mutation-error';
import { leaveOrganization, ownerCount, type IActor } from '../../entities/organization';
import { moveOutOf } from '../../entities/session';
import type { TMembers } from './use-members';

interface ILeaveOrganizationProps {
  members: TMembers;
  actor: IActor | null;
  organizationId: string | undefined;
  organizationName: string | undefined;
}

const leaveBlock = (actor: IActor | null, owners: number): string | null => {
  if (!actor) return null;
  if (actor.role === 'owner' && owners <= 1) {
    return 'You are the only owner. Make someone else an owner first, or delete the organization.';
  }
  return null;
};

export const LeaveOrganization = ({
  members,
  actor,
  organizationId,
  organizationName,
}: ILeaveOrganizationProps) => {
  const [confirming, setConfirming] = useState(false);
  const client = useQueryClient();
  const navigate = useNavigate();
  const blocked = leaveBlock(actor, ownerCount(members.members.data ?? []));

  const leave = useMutation({
    mutationFn: async () => {
      if (!actor || !organizationId) return 'switched' as const;
      await leaveOrganization();
      return moveOutOf(organizationId);
    },
    onSuccess: async (where) => {
      client.clear();
      await navigate({ to: where === 'switched' ? '/' : '/sign-in' });
    },
  });

  return (
    <>
      <DangerAction
        title="Leave this organization"
        blocked={blocked}
        action={
          <Button
            size="sm"
            variant="secondary"
            destructive
            disabled={blocked !== null || !actor || !organizationName}
            onClick={() => setConfirming(true)}
          >
            Leave organization
          </Button>
        }
      >
        You lose access to everything in {organizationName ?? 'it'}. To come back, someone has to
        invite you again.
      </DangerAction>

      <ConfirmDelete
        open={confirming}
        onOpenChange={(open) => {
          setConfirming(open);
          if (!open) leave.reset();
        }}
        entity="organization"
        verb="Leave"
        name={organizationName ?? ''}
        note={leave.isError ? <MutationError error={leave.error} /> : undefined}
        waiting={leave.isPending}
        onConfirm={() => leave.mutate()}
      />
    </>
  );
};
